import { Search, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { USE_CATEGORIES } from '../pages/createListingConstants';

/**
 * Modal for browsing and selecting a product template when creating/editing a listing.
 * Extracted from CreateListingPage to keep that component focused on form state.
 */
export default function TemplateSelectorModal({
  open,
  onOpenChange,
  templates,
  categories,
  loadingTemplates,
  templateSearch,
  setTemplateSearch,
  templateCategory,
  setTemplateCategory,
  applyTemplate,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl">Select a Product Template</DialogTitle>
          <DialogDescription>
            Choose from {templates.length} pre-configured templates to quickly create your listing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Search and Filter */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Search templates..."
                className="w-full h-10 pl-10 pr-4 bg-secondary/30 border border-border/40 rounded-lg text-sm"
                data-testid="template-search-input"
              />
            </div>
            <Select value={templateCategory} onValueChange={setTemplateCategory}>
              <SelectTrigger className="w-48 h-10 bg-secondary/30 border-border/40">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Templates Grid */}
          {loadingTemplates ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No templates found
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div
                  key={template.template_id}
                  onClick={() => applyTemplate(template)}
                  className="p-4 rounded-lg border border-border/40 hover:border-accent/40 hover:bg-accent/5 cursor-pointer transition-all group"
                  data-testid={`template-${template.template_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                      {template.name}
                    </h3>
                    <span className="text-xs text-muted-foreground px-2 py-1 rounded bg-secondary/30">
                      {template.usage_count || 0} uses
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {template.description}
                  </p>

                  {/* Tags with Use Categories highlighted */}
                  <div className="flex flex-wrap gap-2 text-xs mb-2">
                    {template.category && (
                      <span className="px-2 py-1 rounded bg-accent/10 text-accent font-medium">
                        {categories.find(c => c.id === template.category)?.name || template.category}
                      </span>
                    )}
                    {template.use_categories && template.use_categories.slice(0, 3).map((uc) => (
                      <span key={uc} className="px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20">
                        {USE_CATEGORIES.find(c => c.id === uc)?.name || uc}
                      </span>
                    ))}
                    {template.use_categories && template.use_categories.length > 3 && (
                      <span className="px-2 py-1 rounded bg-primary/10 text-primary">
                        +{template.use_categories.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Material and Dimensions */}
                  <div className="flex flex-wrap gap-2 text-xs">
                    {template.default_material && (
                      <span className="px-2 py-1 rounded bg-secondary/50 text-muted-foreground">
                        {template.default_material}
                      </span>
                    )}
                    {template.default_dimensions && (
                      <span className="px-2 py-1 rounded bg-secondary/50 text-muted-foreground">
                        {template.default_dimensions}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
